import React, { useEffect, useRef, useState } from 'react';
import { FaChevronDown, FaCheck } from 'react-icons/fa';
import { usePais } from './context/PaisContext';
import PAISES_SOPORTADOS from './paisesSoportados';
import BanderaPais from './BanderaPais';

// Id estable del contenedor del selector, montado una sola vez en el
// Navbar. Selector de país: un único componente para que:
//   - aparezca ANTES del formulario de login (el Navbar se renderiza por
//     encima del área de login en el layout).
//   - siga visible y usable después del login, listo para que futuros
//     módulos lean el país elegido vía usePais() sin duplicar este control.
// Usa PaisContext.pais (ya normalizado a un país soportado) como valor
// preseleccionado — si la geolocalización detectó Costa Rica, el combo ya
// arranca en Costa Rica; el usuario lo cambia con un solo click.
export const ID_SELECTOR_PAIS_NAVBAR = 'selector-pais-navbar';

// Menú desplegable propio (no <select> nativo): un <select> no permite
// mostrar bandera + nombre + código por opción en la mayoría de
// navegadores, y el diseño pedido lo requiere. Implementa el patrón ARIA
// "listbox" (botón con aria-haspopup/aria-expanded + lista role="listbox"
// con opciones role="option") con navegación de teclado completa.
const MARGEN_VIEWPORT = 16;
const ANCHO_MENU = 240;

// El aviso de "puedes cambiar tu país" vive DENTRO de este mismo componente
// (no como un elemento hermano nuevo en App.js/AppCentral.js): un tooltip
// contextual anclado al control real evita el problema típico de un tip
// "flotante y desconectado" — aparece justo donde el usuario puede actuar,
// se muestra una vez por carga, se autocierra a los 5-8s, y se puede cerrar
// a mano.
const DURACION_AVISO_MS = 6500;
let avisoYaMostradoEnEstaCarga = false;

const ESTILO_CSS = `
  .selector-pais-boton {
    transition: background-color .15s ease, border-color .15s ease, box-shadow .15s ease, transform .15s ease;
  }
  .selector-pais-boton:hover {
    background-color: #FFFFFF;
    box-shadow: 0 4px 12px rgba(0,0,0,0.14);
    transform: translateY(-1px);
  }
  .selector-pais-boton:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(135,36,52,0.35);
  }
  .selector-pais-boton__chevron {
    transition: transform .2s ease;
  }
  .selector-pais-boton--abierto .selector-pais-boton__chevron {
    transform: rotate(180deg);
  }
  @keyframes selectorPaisPulso {
    0% { box-shadow: 0 0 0 0 rgba(135,36,52,0.35); }
    100% { box-shadow: 0 0 0 8px rgba(135,36,52,0); }
  }
  .selector-pais-boton--destacado {
    animation: selectorPaisPulso .5s ease-out;
  }
  @keyframes selectorPaisMenuEntrada {
    from { opacity: 0; transform: translateY(-6px) scale(.97); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }
  .selector-pais-menu {
    animation: selectorPaisMenuEntrada .16s ease-out;
    transform-origin: top;
    max-width: calc(100vw - ${MARGEN_VIEWPORT * 2}px);
  }
  .selector-pais-opcion {
    transition: background-color .12s ease, color .12s ease;
  }
  .selector-pais-opcion:focus-visible {
    outline: 2px solid #872434;
    outline-offset: -2px;
  }
  @keyframes selectorPaisAvisoEntrada {
    from { opacity: 0; transform: translateY(-6px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .selector-pais-aviso {
    animation: selectorPaisAvisoEntrada .25s ease-out;
  }
  .selector-pais-aviso--saliendo {
    opacity: 0 !important;
    transform: translateY(-6px);
    transition: opacity .25s ease, transform .25s ease;
  }
  .selector-pais-aviso__cerrar:hover { opacity: 1 !important; }
  @media (max-width: 480px) {
    .selector-pais-boton__nombre { display: none; }
  }
`;

const ESTILOS = {
  envoltura: { position: 'relative', display: 'inline-block', fontFamily: 'inherit' },
  boton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 14px',
    borderRadius: '12px',
    background: 'rgba(255,255,255,0.92)',
    border: '1px solid rgba(0,0,0,0.08)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
    color: '#26282b',
    fontSize: '0.88rem',
    fontWeight: 600,
    letterSpacing: '0.1px',
    cursor: 'pointer',
    userSelect: 'none',
  },
  nombre: { whiteSpace: 'nowrap' },
  chevron: { color: '#872434', flex: '0 0 auto' },
  menu: {
    position: 'absolute',
    top: 'calc(100% + 8px)',
    width: `${ANCHO_MENU}px`,
    background: '#FFFFFF',
    borderRadius: '14px',
    boxShadow: '0 16px 40px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.08)',
    border: '1px solid rgba(0,0,0,0.06)',
    padding: '8px',
    zIndex: 30,
    listStyle: 'none',
    margin: 0,
  },
  opcion: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 12px',
    borderRadius: '9px',
    cursor: 'pointer',
  },
  opcionTexto: { flex: 1, display: 'flex', flexDirection: 'column', gap: '1px', minWidth: 0 },
  opcionNombre: { fontSize: '0.88rem', fontWeight: 600, color: '#26282b' },
  opcionCodigo: { fontSize: '0.72rem', color: '#8a8d91', letterSpacing: '0.4px' },
  check: { color: '#872434', flex: '0 0 auto' },
  aviso: {
    position: 'absolute',
    top: 'calc(100% + 10px)',
    width: '290px',
    background: '#FFFFFF',
    borderRadius: '10px',
    boxShadow: '0 8px 20px rgba(0,0,0,0.18)',
    borderLeft: '3px solid #872434',
    padding: '12px 12px 12px 14px',
    zIndex: 20,
    display: 'flex',
    gap: '8px',
    alignItems: 'flex-start',
  },
  avisoTexto: { flex: 1, fontSize: '0.8rem', lineHeight: 1.4, color: '#333333' },
  avisoCerrar: {
    background: 'none',
    border: 'none',
    color: '#999999',
    opacity: 0.6,
    cursor: 'pointer',
    padding: '2px',
    flex: '0 0 auto',
  },
};

// Decide si un popover (el menú o el aviso) debe alinearse por la izquierda
// o por la derecha del disparador, según cuál lado tenga espacio real en el
// viewport — evita recortes sin importar en qué punto del layout de cada
// proyecto quede montado el selector (a la izquierda del Navbar en un
// proyecto, a la derecha en otro).
function calcularAlineacion(rectContenedor, anchoPopover) {
  const excedeDerecha = rectContenedor.left + anchoPopover > window.innerWidth - MARGEN_VIEWPORT;
  return excedeDerecha ? 'right' : 'left';
}

export default function SelectorPais({ id, className }) {
  const { pais, establecerPais } = usePais();
  const seleccionActual = PAISES_SOPORTADOS.find((p) => p.codigo === pais) || PAISES_SOPORTADOS[0];

  const [abierto, setAbierto] = useState(false);
  const [alineacionMenu, setAlineacionMenu] = useState('left');
  const [indiceActivo, setIndiceActivo] = useState(0);
  const [destacado, setDestacado] = useState(false);

  const [avisoVisible, setAvisoVisible] = useState(false);
  const [avisoSaliendo, setAvisoSaliendo] = useState(false);
  const [alineacionAviso, setAlineacionAviso] = useState('left');

  const refContenedor = useRef(null);
  const refBoton = useRef(null);
  const refMenu = useRef(null);

  const cerrarAviso = () => {
    setAvisoSaliendo(true);
    setTimeout(() => setAvisoVisible(false), 250);
  };

  useEffect(() => {
    if (refContenedor.current) {
      setAlineacionAviso(calcularAlineacion(refContenedor.current.getBoundingClientRect(), 290));
    }
    if (avisoYaMostradoEnEstaCarga) return;
    avisoYaMostradoEnEstaCarga = true;

    setAvisoVisible(true);
    const temporizador = setTimeout(cerrarAviso, DURACION_AVISO_MS);
    return () => clearTimeout(temporizador);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const abrirMenu = () => {
    if (refContenedor.current) {
      setAlineacionMenu(calcularAlineacion(refContenedor.current.getBoundingClientRect(), ANCHO_MENU));
    }
    setIndiceActivo(Math.max(PAISES_SOPORTADOS.findIndex((p) => p.codigo === seleccionActual.codigo), 0));
    setAbierto(true);
    if (avisoVisible) cerrarAviso(); // interactuar con el control ya cumple el propósito del aviso
  };

  const cerrarMenu = (devolverFoco) => {
    setAbierto(false);
    if (devolverFoco && refBoton.current) refBoton.current.focus();
  };

  const seleccionar = (paisOpcion) => {
    if (paisOpcion.codigo !== seleccionActual.codigo) {
      establecerPais(paisOpcion.codigo);
      setDestacado(true);
      setTimeout(() => setDestacado(false), 500);
    }
    cerrarMenu(true);
  };

  // Cierra al hacer click fuera del control.
  useEffect(() => {
    if (!abierto) return;
    function alClickFuera(evento) {
      if (refContenedor.current && !refContenedor.current.contains(evento.target)) {
        cerrarMenu(false);
      }
    }
    document.addEventListener('mousedown', alClickFuera);
    return () => document.removeEventListener('mousedown', alClickFuera);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto]);

  // Foco en el menú al abrir, para que las flechas funcionen de inmediato.
  useEffect(() => {
    if (abierto && refMenu.current) refMenu.current.focus();
  }, [abierto]);

  const manejarTeclaBoton = (evento) => {
    if (evento.key === 'ArrowDown' || evento.key === 'Enter' || evento.key === ' ') {
      evento.preventDefault();
      abrirMenu();
    }
  };

  const manejarTeclaMenu = (evento) => {
    if (evento.key === 'Escape') {
      evento.preventDefault();
      cerrarMenu(true);
    } else if (evento.key === 'ArrowDown') {
      evento.preventDefault();
      setIndiceActivo((i) => Math.min(i + 1, PAISES_SOPORTADOS.length - 1));
    } else if (evento.key === 'ArrowUp') {
      evento.preventDefault();
      setIndiceActivo((i) => Math.max(i - 1, 0));
    } else if (evento.key === 'Enter' || evento.key === ' ') {
      evento.preventDefault();
      seleccionar(PAISES_SOPORTADOS[indiceActivo]);
    } else if (evento.key === 'Tab') {
      cerrarMenu(false);
    }
  };

  return (
    <div id={id} className={className} ref={refContenedor} style={ESTILOS.envoltura}>
      <style>{ESTILO_CSS}</style>

      <button
        type="button"
        ref={refBoton}
        className={`selector-pais-boton${abierto ? ' selector-pais-boton--abierto' : ''}${destacado ? ' selector-pais-boton--destacado' : ''}`}
        style={ESTILOS.boton}
        onClick={() => (abierto ? cerrarMenu(false) : abrirMenu())}
        onKeyDown={manejarTeclaBoton}
        aria-haspopup="listbox"
        aria-expanded={abierto}
        aria-label={`País: ${seleccionActual.nombre}. Cambiar país`}
      >
        <BanderaPais codigo={seleccionActual.codigo} nombre={seleccionActual.nombre} />
        <span className="selector-pais-boton__nombre" style={ESTILOS.nombre}>{seleccionActual.nombre}</span>
        <FaChevronDown size="11px" className="selector-pais-boton__chevron" style={ESTILOS.chevron} aria-hidden="true" />
      </button>

      {abierto && (
        <ul
          ref={refMenu}
          role="listbox"
          tabIndex={-1}
          aria-activedescendant={`selector-pais-opcion-${PAISES_SOPORTADOS[indiceActivo].codigo}`}
          aria-label="Selecciona tu país"
          className={`selector-pais-menu selector-pais-menu--${alineacionMenu}`}
          style={{ ...ESTILOS.menu, [alineacionMenu]: 0 }}
          onKeyDown={manejarTeclaMenu}
        >
          {PAISES_SOPORTADOS.map((paisOpcion, indice) => {
            const seleccionado = paisOpcion.codigo === seleccionActual.codigo;
            const activo = indice === indiceActivo;
            return (
              <li
                key={paisOpcion.codigo}
                id={`selector-pais-opcion-${paisOpcion.codigo}`}
                role="option"
                aria-selected={seleccionado}
                className="selector-pais-opcion"
                style={{
                  ...ESTILOS.opcion,
                  backgroundColor: activo ? 'rgba(135,36,52,0.08)' : 'transparent',
                }}
                onMouseEnter={() => setIndiceActivo(indice)}
                onClick={() => seleccionar(paisOpcion)}
              >
                <BanderaPais codigo={paisOpcion.codigo} nombre={paisOpcion.nombre} style={{ marginTop: '2px' }} />
                <span style={ESTILOS.opcionTexto}>
                  <span style={ESTILOS.opcionNombre}>{paisOpcion.nombre}</span>
                  <span style={ESTILOS.opcionCodigo}>{paisOpcion.codigo}</span>
                </span>
                {seleccionado && <FaCheck size="12px" style={ESTILOS.check} aria-hidden="true" />}
              </li>
            );
          })}
        </ul>
      )}

      {avisoVisible && (
        <div
          className={`selector-pais-aviso${avisoSaliendo ? ' selector-pais-aviso--saliendo' : ''}`}
          style={{ ...ESTILOS.aviso, [alineacionAviso]: 0 }}
          role="status"
        >
          <span style={ESTILOS.avisoTexto}>
            Puedes cambiar tu país aquí en cualquier momento para personalizar la información que ves.
          </span>
          <button
            type="button"
            className="selector-pais-aviso__cerrar"
            style={ESTILOS.avisoCerrar}
            onClick={cerrarAviso}
            aria-label="Cerrar aviso"
          >
            <FaChevronDown size="0" style={{ display: 'none' }} />
            ✕
          </button>
        </div>
      )}
    </div>
  );
}
